import { InputField, SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import { Button } from '@advanza/ui'
import NiceModal, { useModal } from '@ebay/nice-modal-react'
import BaseModal, {
    BaseModalFooter,
    BaseModalHeader,
    BaseModalMainContent,
} from '@trustoo/components/modal/BaseModal'
import { addRolloverDiscount, fetchPresenceSubscription } from 'actions/providers'
import { useModalToast, useToast } from 'components/misc/Toaster'
import { useAppDispatch } from 'hooks/hooks'
import {
    RolloverDiscountRequest,
    TYPE_AMOUNT,
    TYPE_PERCENTAGE,
} from 'interfaces/PresenceSubscription'
import { useState } from 'react'

interface AddPresenceRolloverDiscountModalProps {
    providerId: number
}

const AddPresenceRolloverDiscountModal = NiceModal.create(
    ({ providerId }: AddPresenceRolloverDiscountModalProps) => {
        const modal = useModal()
        const dispatch = useAppDispatch()
        const { addToast } = useToast()
        const { addModalToast, deleteAllToasts: clearModalToasts } = useModalToast()
        const [monthsCount, setMonthsCount] = useState(1)
        const [discountType, setDiscountType] = useState<
            typeof TYPE_AMOUNT | typeof TYPE_PERCENTAGE
        >(TYPE_PERCENTAGE)
        const [discountAmount, setDiscountAmount] = useState(100)
        const [expiryDate, setExpiryDate] = useState('')
        const [isSubmitting, setIsSubmitting] = useState(false)

        const handleSubmit = async () => {
            setIsSubmitting(true)
            // Drop a previous failure as the retry starts, so the message never outlives its cause.
            clearModalToasts()

            // Validation
            if (monthsCount < 1 || monthsCount > 12) {
                addModalToast({
                    id: 'save-error',
                    msg: 'Months count must be between 1 and 12',
                    icon: 'error',
                    red: true,
                })
                setIsSubmitting(false)
                return
            }

            if (discountAmount < 0) {
                addModalToast({
                    id: 'save-error',
                    msg: 'Discount amount must be positive',
                    icon: 'error',
                    red: true,
                })
                setIsSubmitting(false)
                return
            }

            const requestData: RolloverDiscountRequest = {
                months_count: monthsCount,
                discount_type: discountType,
                discount_amount: discountAmount,
                expiry_date: expiryDate || undefined,
            }

            try {
                await dispatch(addRolloverDiscount(providerId, requestData))

                // Refresh the presence subscription data
                dispatch(fetchPresenceSubscription(providerId))

                addToast({
                    msg: 'Rollover discount added successfully',
                    green: true,
                    icon: 'check_circle',
                })

                modal.hide()
            } catch (error: any) {
                // Stays in the modal: nothing was added, and the button is the retry.
                addModalToast({
                    id: 'save-error',
                    msg: error.error || 'Failed to add rollover discount',
                    icon: 'error',
                    red: true,
                })
            } finally {
                setIsSubmitting(false)
            }
        }

        const handleReset = () => {
            setMonthsCount(3)
            setDiscountType('percentage')
            setDiscountAmount(100)
            setExpiryDate('')
            clearModalToasts()
        }

        return (
            <BaseModal>
                <BaseModalHeader>
                    <h2 className="text-display-xs font-bold">Add Rollover Discount Offer</h2>
                </BaseModalHeader>

                <BaseModalMainContent>
                    <div className="space-y-4">
                        <div className="form-group">
                            <label className="block text-md font-medium text-neutral-700 mb-1">
                                Months of Discount
                            </label>
                            <SelectSimple
                                value={monthsCount}
                                onChange={(e: SelectSimpleChangeEvent) =>
                                    setMonthsCount(parseInt(String(e.target.value || '1')))
                                }
                                options={[
                                    { value: 1, name: '1 month' },
                                    { value: 2, name: '2 months' },
                                    { value: 3, name: '3 months' },
                                    { value: 4, name: '4 months' },
                                    { value: 5, name: '5 months' },
                                    { value: 6, name: '6 months' },
                                    { value: 7, name: '7 months' },
                                    { value: 8, name: '8 months' },
                                    { value: 9, name: '9 months' },
                                    { value: 10, name: '10 months' },
                                    { value: 11, name: '11 months' },
                                    { value: 12, name: '12 months' },
                                ]}
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-md font-medium text-neutral-700 mb-1">
                                Discount Type
                            </label>
                            <SelectSimple
                                value={discountType}
                                onChange={(e: SelectSimpleChangeEvent) =>
                                    setDiscountType(
                                        e.target.value as
                                            | typeof TYPE_AMOUNT
                                            | typeof TYPE_PERCENTAGE,
                                    )
                                }
                                options={[
                                    { value: TYPE_PERCENTAGE, name: 'Percentage' },
                                    { value: TYPE_AMOUNT, name: 'Fixed Amount' },
                                ]}
                            />
                        </div>

                        <div className="form-group">
                            <label className="block text-md font-medium text-neutral-700 mb-1">
                                Discount Amount
                            </label>
                            <InputField
                                type="number"
                                value={discountAmount}
                                onChange={(e) => setDiscountAmount(parseFloat(e.target.value) || 0)}
                                min="0"
                                step={discountType === TYPE_PERCENTAGE ? '1' : '0.01'}
                            />
                            <small className="text-neutral-500 mt-1 block">
                                {discountType === TYPE_PERCENTAGE
                                    ? 'Percentage (e.g., 100 for 100%)'
                                    : 'Fixed amount in currency'}
                            </small>
                        </div>

                        <div className="form-group">
                            <label className="block text-md font-medium text-neutral-700 mb-1">
                                Expiry Date (optional)
                            </label>
                            <InputField
                                type="date"
                                value={expiryDate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                            />
                            <small className="text-neutral-500 mt-1 block">
                                Defaults to 1 month from now if not specified
                            </small>
                        </div>
                    </div>
                </BaseModalMainContent>

                <BaseModalFooter>
                    <div className="flex gap-2">
                        <Button type="button" onClick={handleReset} disabled={isSubmitting}>
                            Reset
                        </Button>
                        <Button type="button" onClick={() => modal.hide()} disabled={isSubmitting}>
                            Cancel
                        </Button>
                        <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
                            {isSubmitting ? 'Adding...' : 'Add Discount Offer'}
                        </Button>
                    </div>
                </BaseModalFooter>
            </BaseModal>
        )
    },
)

export default AddPresenceRolloverDiscountModal
